// VERA right inside Records: a small conversation beside the list, about what's on screen. She runs the whole CRM from
// here (find, add, change, link, new fields and dropdown choices, undo) with the same tools and rules as everywhere,
// and the list updates as she works. Every change she makes shows below her answer with its own Undo (owners).

import { useEffect, useRef, useState, type ClipboardEvent } from "react";
import { Icon, Orb, useStudio } from "./ui";
import { Markdown } from "./Markdown";
import { vera, type Attachment } from "../api/vera";
import { crm, CRM_CHANGED, type CrmChange, type CrmRecord, type CrmType } from "../lib/crm";
import { pastedFiles, readFiles } from "../lib/attachments";
import { T, Tf } from "../lib/i18n";
import type { ScopeId } from "../config/model";

type Msg = { role: "user" | "vera"; text: string; changes?: CrmChange[]; files?: string[] };

export function RecordsVera({ type, record, title, owner, start, onChanged, onClose, heading, where: screen, ideas: given }: {
  type?: CrmType; record?: CrmRecord | null; title?: string; owner: boolean; start?: string | null;
  onChanged: () => void; onClose: () => void;
  /** Any other screen (Connections, Operations): its name under VERA, what VERA is told is on screen, and three starts. */
  heading?: string; where?: string; ideas?: string[];
}) {
  const { getToken, profile, go, toast } = useStudio();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState(start ?? "");
  const [files, setFiles] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const box = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { if (start) setText(start); box.current?.focus(); }, [start]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs, busy]);
  const scope = (profile.scopes[0] ?? "all") as ScopeId;

  async function send() {
    const q = text.trim();
    if ((!q && !files.length) || busy) return;
    const sent = files;
    setMsgs((m) => [...m, { role: "user", text: q, files: sent.map((f) => f.name) }]);
    setText(""); setFiles([]); setBusy(true); setStep("");
    const since = Date.now() - 2000;
    // VERA hears what's on screen, so "this one" and "add a field here" just work.
    // For VERA only (in English, like the rest of her context): never shown as the person's words.
    const where = screen ? `(On screen: ${screen}.)` : type && record && title ? `(On screen: Records, ${type.plural}, the ${type.name.toLowerCase()} "${title}".)` : type ? `(On screen: Records, the ${type.plural} list.)` : "";
    try {
      const r = await vera.ask({ text: `${q || "Look at what I attached."}\n\n${where}`, scope, attachments: sent, history: msgs.map((m) => ({ role: m.role, text: m.text })).slice(-10), onStep: setStep }, getToken);
      let changes: CrmChange[] = [];
      try { changes = (await crm.changes(getToken)).filter((c) => c.at >= since && c.via !== "studio" && c.action !== "undo"); } catch { /* the answer still shows */ }
      setMsgs((m) => [...m, { role: "vera", text: r.text, changes }]);
      if (changes.length) { onChanged(); window.dispatchEvent(new Event(CRM_CHANGED)); }
    } catch (e) {
      setMsgs((m) => [...m, { role: "vera", text: e instanceof Error ? e.message : T("VERA's server didn't answer. Please try again.") }]);
    } finally { setBusy(false); setStep(""); }
  }

  async function undo(i: number, c: CrmChange) {
    try {
      await crm.undo(getToken, c.id);
      setMsgs((m) => m.map((x, j) => (j === i ? { ...x, changes: x.changes?.map((y) => (y.id === c.id ? { ...y, undoneBy: "you" } : y)) } : x)));
      toast(T("Undone.")); onChanged(); window.dispatchEvent(new Event(CRM_CHANGED));
    } catch (e) { toast(e instanceof Error ? e.message : T("That didn't work. Please try again.")); }
  }

  async function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const list = pastedFiles(e.clipboardData);
    if (!list.length) return;
    e.preventDefault();
    const r = await readFiles(list, files.length);
    setFiles((f) => [...f, ...r.files]);
  }

  const ideas = given ?? (!type ? [] : record
    ? [T("What's missing here?"), T("Fill in what you can"), T("Add a field for this")]
    : [Tf("How do my {list} look?", { list: type.plural.toLowerCase() }), Tf("Which {list} are missing details?", { list: type.plural.toLowerCase() }), Tf("Add a new {kind}", { kind: type.name.toLowerCase() })]);

  return (
    <aside className="drawer recvera" aria-label={T("VERA in your records")}>
      <div className="row between">
        <div className="row" style={{ gap: 8 }}><Orb size={18} mode={busy ? "think" : "breathe"} /><div><b>VERA</b><div className="faint small">{heading ?? (type ? (record && title ? `${type.name}: ${title}` : type.plural) : "")}</div></div></div>
        <div className="row" style={{ gap: 4 }}>
          <button className="iconbtn ghost" title={T("Continue in Workspace")} aria-label={T("Continue in Workspace")} onClick={() => go("workspace", { ask: msgs.length ? undefined : text || undefined })}><Icon name="expand" size={15} /></button>
          <button className="iconbtn ghost" onClick={onClose} aria-label={T("Close")}><Icon name="x" /></button>
        </div>
      </div>
      <div className="recvera-msgs">
        {!msgs.length && (
          <div className="stack" style={{ gap: 8 }}>
            <p className="mute" style={{ margin: 0 }}>{type && !screen ? T("Tell me anything about your work: I'll put every detail in the right place, link it, and add fields or choices when you need new ones.") : T("Ask me anything about this page, or tell me what you need.")}</p>
            <div className="suggest" style={{ justifyContent: "flex-start" }}>{ideas.map((s) => <button key={s} className="btn sm" onClick={() => { setText(s); box.current?.focus(); }}>{s}</button>)}</div>
          </div>)}
        {msgs.map((m, i) => m.role === "user"
          ? <div key={i} className="msg me"><div>{m.text}</div>{!!m.files?.length && <div className="faint small">{m.files.join(", ")}</div>}</div>
          : (
            <div key={i} className="msg vera">
              <Markdown text={m.text} />
              {!!m.changes?.length && (
                <ul className="recvera-changes">
                  {m.changes.map((c) => (
                    <li key={c.id} className={c.undoneBy ? "undone" : ""}>
                      <Icon name="check" size={12} /> <span className="grow">{c.summary}</span>
                      {owner && !c.undoneBy && <button className="linkbtn" onClick={() => void undo(i, c)}>{T("Undo")}</button>}
                      {c.undoneBy && <span className="faint small">{T("undone")}</span>}
                    </li>))}
                </ul>)}
            </div>))}
        {busy && <div className="msg vera working"><div className="row" style={{ gap: 8 }}><Orb size={12} mode="think" /><span className="faint">{step || T("VERA is on it…")}</span></div></div>}
        <div ref={end} />
      </div>
      {!!files.length && <div className="row wrap" style={{ gap: 6 }}>{files.map((f, i) => <span key={i} className="chip on">{f.name}<button className="linkbtn" aria-label={T("Remove")} onClick={() => setFiles((x) => x.filter((_, j) => j !== i))}><Icon name="x" size={11} /></button></span>)}</div>}
      <div className="recvera-compose">
        <textarea ref={box} rows={2} value={text} placeholder={screen || !type ? T("Ask VERA…") : record ? T("Tell VERA about this one…") : Tf("Tell VERA about your {list}…", { list: type.plural.toLowerCase() })}
          onChange={(e) => setText(e.target.value)} onPaste={(e) => void onPaste(e)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} />
        <button className="btn primary" disabled={busy || (!text.trim() && !files.length)} onClick={() => void send()} aria-label={T("Send")}><Icon name="send" size={15} /></button>
      </div>
    </aside>
  );
}
